import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {IUser, UserAuthorities, UserStatus, UserStatusEnum} from '../shared/model/user.model';
import {Berater} from '../shared/model/berater.model';
import {catchError, Observable, throwError} from 'rxjs';

export interface PatchUserAsAdminDTO {
  firstName: string;
  lastName: string;
  langKey: string;
  email: string;
  authorities?: UserAuthorities[];
  superVisorId?: string;
  status?: UserStatusEnum;
  password?: string;
  abbreviation?: string;
  cost?: number;
  companyId?: string | null;
}

export interface PageResponse<T> {
  content: T[];
  page: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}

export interface PatchConsultantAsAdminDTO {
  vorname: string;
  nachname: string;
}

@Injectable({providedIn: 'root'})
export class AdminService {
  endpointUri = environment.apiConfig.uri + 'api/admin/users';

  constructor(private http: HttpClient) {
  }

  getUsersPage(params: any, page: number, size: number) {
    return this.http.get<PageResponse<IUser>>(this.endpointUri, {
      params: {...params, page, size}
    });
  }

  register(user: IUser) {
    // `authorities` gehoert bewusst nicht mehr in den Body. Das Backend (NP20-947)
    // vergibt beim Anlegen fix die Rolle User und lehnt eine mitgeschickte Liste mit 400 ab —
    // auch eine leere.
    const dto = {
      firstName: user.firstName,
      lastName: user.lastName,
      langKey: user.langKey,
      email: user.email,
      superVisorId: user.superVisor?.id,
      status: user.status ?? UserStatusEnum.INACTIVE,
      abbreviation: user.abbreviation,
      cost: user.cost,
      companyId: user.companyId ?? null,
      password: user.password || this.generatePassword(),
    };
    return this.http.post(this.endpointUri, dto);
  }

  private generatePassword(): string {
    // Registrieren erwartet ein Passwort; der neue Modal-Flow sammelt keins mehr —
    // ein Zufallswert erfüllt die BE-Constraint, das echte Passwort wird per Reset gesetzt.
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('') + '!Aa1';
  }

  setStatus(status: UserStatus, id: string) {
    return this.http.post(this.endpointUri + '/' + id + '/status', status);
  }

  updateUser(user: IUser) {
    const baseDto: PatchUserAsAdminDTO = {
      firstName: user.firstName,
      lastName: user.lastName,
      langKey: user.langKey,
      email: user.email,
      authorities: user.authorities,
      superVisorId: user.superVisor?.id,
      status: user.status,
      companyId: user.companyId ?? null,
    };

    // abbreviation und cost werden BE-seitig am Consultant-Profil gepflegt. Wenn
    // der User keinen (oder einen verwaisten) Consultant-Datensatz hat, führt das
    // Mitsenden zu 404 "Consultant not found". Deshalb: wenn keine consultantId
    // vorliegt, gar nicht erst mitsenden; und bei einem 404 hier trotzdem einmal
    // ohne consultant-Felder retry-en, damit die restlichen User-Änderungen (Firma,
    // Betreuer, Rollen, Status) nicht mitscheitern.
    const fullDto: PatchUserAsAdminDTO = user.consultantId
      ? {...baseDto, abbreviation: user.abbreviation, cost: user.cost}
      : baseDto;

    return this.http.patch(`${this.endpointUri}/${user.id}`, fullDto).pipe(
      catchError((err: HttpErrorResponse) => {
        const consultantMissing = err?.status === 404
          && (err.error?.title === 'Consultant not found'
            || err.error?.message?.includes?.('Consultant not found'));
        if (consultantMissing && user.consultantId) {
          return this.http.patch(`${this.endpointUri}/${user.id}`, baseDto);
        }
        return throwError(() => err);
      }),
    );
  }

  /**
   * Nur Umhaengen, kein Leeren: fuer `superVisorId` gibt es kein Loeschsignal — das Backend
   * ignoriert `null` (Mapper-Strategie IGNORE) und weist einen Leerstring als unbekannten
   * Betreuer ab.
   *
   * `authorities` bleibt bewusst weg: ein fehlender Wert heisst "unveraendert", und ein
   * Umhaengen darf nicht am Rollen-Gate haengen — Office bedient diesen Pfad in einer
   * Schleife ueber viele Nutzer.
   */
  reassignSupervisor(user: IUser, newSuperVisorId: string) {
    const dto = {
      firstName: user.firstName,
      lastName: user.lastName,
      langKey: user.langKey,
      email: user.email,
      superVisorId: newSuperVisorId,
      abbreviation: user.abbreviation,
      cost: user.cost,
      companyId: user.companyId ?? null,
    };
    return this.http.patch(`${this.endpointUri}/${user.id}`, dto);
  }

  /**
   * `superVisorId` muss mit: das Backend liest einen fehlenden Wert als "unveraendert", nicht
   * als "leeren". Die Firma leert allein der Leerstring, `null` ist auch dort "unveraendert".
   *
   * `authorities` bleibt weg — siehe `reassignSupervisor`.
   */
  reassignCompany(user: IUser, newCompanyId: string | null) {
    const dto: PatchUserAsAdminDTO = {
      firstName: user.firstName,
      lastName: user.lastName,
      langKey: user.langKey,
      email: user.email,
      superVisorId: user.superVisor?.id ?? user.superVisorId,
      status: user.status,
      companyId: newCompanyId ?? '',
    };
    return this.http.patch(`${this.endpointUri}/${user.id}`, dto);
  }

  deleteUser(id: string) {
    return this.http.delete(`${this.endpointUri}/${id}`);
  }

  editConsultant(consultant: Berater) {
    const dto: PatchConsultantAsAdminDTO = {
      vorname: consultant.vorname,
      nachname: consultant.nachname
    };
    return this.http.patch(
      environment.apiConfig.uri + 'api/admin/consultant/' + consultant.id,
      dto
    );
  }

  getUsersByIds(userIds: string[]): Observable<IUser[]> {
    return this.http.post<IUser[]>(
      environment.apiConfig.uri + 'api/admin/users/by-ids',
      {userIds}
    );
  }
}
